import React, { useState, useEffect } from 'react';
import { FaUser, FaClock, FaBrain, FaPlus, FaTimes } from 'react-icons/fa';
import { CgFileDocument } from "react-icons/cg";
import { MdAccessibilityNew } from 'react-icons/md';
import '../Therapy/PatientAssessment.css';
import { useLocation } from 'react-router-dom';
import { APIURL } from '../../../Global';
import axios from "axios";
import { LuActivity, LuBone, LuBrain } from "react-icons/lu";
import HemoDialysisFlowSheet from './HemoDialysisFlowSheet.js';
import DocumentPopup from '../Therapy/DocumentPopup.js';
import { useAlert } from 'react-alert';

const DialysisDashboard = () => {
    const location = useLocation()
    const [showAssessment, setShowAssessment] = useState(false)
    const [scores, setScores] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectedDocument, setSelectedDocument] = useState(null)
    const [showDocumentPopup, setShowDocumentPopup] = useState(false)
    const [patientInfoLoaded, setPatientInfoLoaded] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0)
    const [userInfo, setUserInfo] = useState({
        patient_id: location?.state && location.state.id,
        dialysis_appointment: location?.state && location.state.dialysis_id,
        inpatient: location?.state?.inpatient,
        mob: location?.state?.mob,
    });
    sessionStorage.setItem('previousPath', '/tilt');
    const alert = useAlert()

    useEffect(() => {
        const loadData = async () => {
            await patientInfo();
            fetchScores();
        };
        loadData();
    }, []);

    const patientInfo = async () => {
        const tokenString = sessionStorage.getItem("usertoken");
        let str = tokenString.replace(/["]+/g, "");

        try {
            const res = await axios.get(`${APIURL}/api/v1/doctor/patient-dashboard/?patient_id=${userInfo.patient_id}`, {
                headers: {
                    Authorization: "Token " + str,
                },
            });

            if (res.data.status === "success") {
                setUserInfo(prev => ({
                    ...prev,
                    name: res.data.data.name,
                    age: res.data.data.age,
                    gender: res.data.data.gender,
                    photo: res.data.data.photo
                }));
                setPatientInfoLoaded(true);
            }
        } catch (error) {
            console.error("Error fetching patient info:", error);
            setPatientInfoLoaded(true);
        }
    };

    const fetchScores = async () => {
        setLoading(true);
        const tokenString = sessionStorage.getItem("usertoken");
        let str = tokenString.replace(/["]+/g, "");

        try {
            const response = await axios.get(`${APIURL}/api/v1/patient/haemodialysis/?patient_id=${userInfo.patient_id}`, {
                headers: {
                    Authorization: "Token " + str,
                },
            });

            if (response.data.status === "success") {
                const sortedScores = response.data.message.slice().reverse();
                setScores(sortedScores);

                // Auto-select the latest ongoing item and show assessment
                const latestItem = sortedScores[0];
                if (latestItem && latestItem.status === "ongoing") {
                    setSelectedDocument(latestItem);
                    setShowAssessment(true);
                } else {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                    setSelectedDocument(null)
                    setShowAssessment(false)
                }
            } else {
                setScores([]);
            }
        } catch (error) {
            alert.error("Error fetching details:", error);
            setScores([]);
        } finally {
            setLoading(false);
        }
    };

    const CircularText = ({ text }) => {
        return (
            <div className="text">
                <div className={`text-background${userInfo?.inpatient ? ' text-background2' : ' text-background1'}`}></div>
                <p>
                    {text?.split('').map((char, i) => (
                        <span
                            key={i}
                            style={{
                                fontSize: '10px',
                                fontWeight: 'bold',
                                transform: `rotate(${i * (180 / text.length) + (80 - i * 36)}deg)`,
                                position: 'absolute',
                                left: '50%',
                                bottom: '0',
                                transformOrigin: '0 0',
                                height: '40px',
                                color: 'white'
                            }}
                        >
                            {char}
                        </span>
                    ))}
                </p>
            </div>
        );
    };

    const handleDocumentClick = (score) => {
        setSelectedDocument(score);
        if (score?.dialysis_terminated_by) {
            setShowDocumentPopup(true);
        } else {
            setShowAssessment(true)
        }
    }

    const closeDocumentPopup = () => {
        setShowDocumentPopup(false);
        setSelectedDocument(null);
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    const hasDocument = (score) => {
        return score.scale_document && score.scale_document !== '{}';
    };

    const getDocumentInfo = (score) => {
        if (!hasDocument(score)) return null;
        try {
            return JSON.parse(score.scale_document);
        } catch (e) {
            return null;
        }
    };

    const onAssessmentComplete = (type) => {
        if (type === 'cancel') {
            setSelectedDocument(null)
            setShowAssessment(false)
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        } else {
            setRefreshTrigger(prev => prev + 1);
            fetchScores();
        }
    };

    return (
        <div className="dashboard-container">
            <div className="patient-header">
                <div className="sub-section-img" style={{ paddingleft: "5%" }}>
                    <div className="circle">
                        <img
                            src={userInfo.photo}
                            alt="Profile"
                            style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                position: 'relative',
                                zIndex: 3
                            }}
                        />
                        <CircularText text={userInfo?.inpatient ? "INPATIENT" : "OUTPATIENT"} />
                    </div>
                </div>
                <div>
                    <div className="patient-title">
                        <h1>Patient: {userInfo.name}</h1>
                    </div>
                    <p className="patient-info">
                        Age: {userInfo.age} | Gender: {userInfo.gender}
                    </p>
                </div>
            </div>

            <div className="assessment-section">
                <div className="section-title" style={{ justifyContent: 'space-between' }}>
                    <div className='section-title-content'>
                        <FaClock className="header-icon" />
                        <h2>Dialysis</h2>
                    </div>
                    <button className="new-assessment-btn" onClick={() => { setSelectedDocument(null); !showAssessment && window.scrollTo({ top: 1500, left: 0, behavior: 'smooth' }); setShowAssessment(!showAssessment); }} style={{ width: '20%' }}>
                        <FaPlus className="btn-icon" />
                        Add New
                    </button>
                </div>
                <div style={{ display: 'flex' }}>
                    <div className="assessment-list-section col-12" style={{ height: (scores.length === 0 || showAssessment) && '200px' }}>
                        <div className="list-container">
                            <div className="list-header">
                                <h3>Dialysis History</h3>
                            </div>
                            <div className="list-content">
                                {loading ? (
                                    <div className="loading-message">
                                        Loading data...
                                    </div>
                                ) : scores.length === 0 ? (
                                    <div className="empty-message">
                                        No data found!!
                                    </div>
                                ) : (
                                    scores.map((score, index) => {
                                        const documentInfo = getDocumentInfo(score);
                                        const isSelected = selectedDocument && selectedDocument.id === score.id;
                                        return (
                                            <div
                                                key={score.id || index}
                                                onClick={() => handleDocumentClick(score)}
                                                className={`assessment-item score-item ${isSelected ? 'selected' : ''}`}
                                            >
                                                <div className="item-content">
                                                    <div className="item-details">
                                                        <p className="item-date">
                                                            {formatDate(score?.dialysis_run_date)}
                                                        </p>
                                                        <p className="item-score">
                                                            Status: {score?.status === "completed" ? "Completed" : score?.status === "terminated" ? "Terminated" : "Ongoing"}
                                                        </p>
                                                        {documentInfo && (
                                                            <div className="document-info">
                                                                <CgFileDocument className="document-icon" />
                                                                <span className="document-title">
                                                                    {documentInfo.title || 'Assessment Document'}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="item-action document-action">
                                                        <CgFileDocument />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showDocumentPopup && selectedDocument && (
                <DocumentPopup
                    id={userInfo.patient_id}
                    document={selectedDocument}
                    onClose={closeDocumentPopup}
                    type='dialysis_doc'
                />
            )}

            {showAssessment && patientInfoLoaded && (
                <HemoDialysisFlowSheet
                    data={{ ...userInfo, ...selectedDocument }}
                    onComplete={onAssessmentComplete}
                    onClose={() => setShowAssessment(false)}
                />
            )}
            {showAssessment && !patientInfoLoaded && (
                <div className="loading-message">
                    Loading patient information...
                </div>
            )}
        </div>
    );
};

export default DialysisDashboard;